import { DetailSubsection } from '@periscopes/general';
import { SuggestPlaintextCellEditor } from './SuggestPlaintextCellEditor';
import { When } from 'react-if';

import { AutodocType, FeatureFlag, patchSalesforceSonarObject } from '@sonar-software/sonar-core';
import { COPY_SPECIFIC, PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import {
	genericProcessingFailure,
	ProcessingResult,
	useBoolean,
	useFeatureFlag,
	useProcessing,
	useSonarObjectReference,
	useSuggestionEditor,
	useUpdateSonarObjectReference
} from '@hooks';
import { ProcessingIcon, SuggestionButton } from '@ui/general';
import React, { useCallback, useEffect } from 'react';
import { SuggestPlaintextEditor, SuggestPlaintextEditorProps } from './SuggestPlaintextEditor';

async function updateHelpText(sonarObjectId: number, helpText: string): Promise<ProcessingResult> {
	const result = await patchSalesforceSonarObject({
		helpText,
		sonarObjectId
	});

	return result.data ?? genericProcessingFailure;
}

export interface SalesforceHelpTextProps {
	isSpreadsheet?: boolean;
	sonarObjectId: number;
}

export const SalesforceHelpText: React.FC<SalesforceHelpTextProps> = ({ isSpreadsheet, sonarObjectId }) => {
	const { reset, start, state, stop } = useProcessing();
	const { salesforce } = useSonarObjectReference(sonarObjectId);
	const { update } = useUpdateSonarObjectReference();
	const isEnabled = useFeatureFlag(FeatureFlag.DataDictionary);
	const [ processingDone, setProcessingDone ] = useBoolean(true);
	const autodocType = AutodocType.HelpText;

	const { helpText, managedPackage } = salesforce ?? {};

	const isReadOnly = Boolean(managedPackage);
	const isDisabled = !isEnabled || isReadOnly || state.isProcessing;

	const suggestion = useSuggestionEditor({
		autodocType,
		isReadOnly,
		sonarObjectId,
		value: helpText
	});

	useEffect(() => {
		if (!state.isProcessing && state.result) {
			setTimeout(setProcessingDone.on, 5000);
		} else if (state.isProcessing) {
			setProcessingDone.off();
		}
	}, [ setProcessingDone, state.isProcessing, state.result ]);

	const handleSave = useCallback(
		async(value: string) => {
			if (value === helpText || (!value && !helpText)) {
				return;
			}

			start();
			const updateResult = await updateHelpText(sonarObjectId, value);

			if (updateResult?.success) {
				update({
					salesforce: { helpText: value },
					sonarObjectId
				});
			}

			stop(updateResult);
		},
		[ helpText, sonarObjectId, start, stop, update ]
	);

	const editorProps: SuggestPlaintextEditorProps = {
		allowNewline: true,
		autodocType,
		canBeEmpty: true,
		isDisabled,
		isProcessing: state.isProcessing,
		isReadOnly,
		isSalesforce: true,
		maxLength: 510,
		onSave: handleSave,
		onStartEditing: reset,
		placeholder: isSpreadsheet ? undefined : PLACEHOLDERS_COPY.ADD_HELPTEXT,
		processingResult: state.result,
		saveOnBlur: true,
		sonarObjectId,
		suggestion,
		tooltip: isEnabled && isReadOnly ? COPY_SPECIFIC.UNABLE_TO_UDPATE_MANAGED_SALESFORCE : undefined,
		value: helpText
	};

	if (isSpreadsheet) {
		return <SuggestPlaintextCellEditor { ...editorProps } />;
	}

	const showSuggestButton = processingDone && !suggestion.isEditing && !suggestion.value && !suggestion.loading;

	return (
		<DetailSubsection
			title={ COPY_SPECIFIC.HELP_TEXT }
			titleExtra={
				<>
					<When condition={ showSuggestButton }>
						<SuggestionButton
							autodocType={ autodocType }
							onClick={ suggestion.request } />
					</When>
					<ProcessingIcon
						isProcessing={ state.isProcessing }
						isSalesforce
						result={ state.result } />
				</>
			}>
			<SuggestPlaintextEditor { ...editorProps } />
		</DetailSubsection>
	);
};
